import React from "react";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import type { TicketMessage } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Reply } from "lucide-react";

type InboundEmailMessage = TicketMessage & {
  expediteur?: string | null;
  destinataire?: string | null;
  objet?: string | null;
  nbPiecesJointes?: number | null;
  direction?: string | null;
  source?: string | null;
};

export function TicketInboundEmail({
  message,
  onReply,
}: {
  message: InboundEmailMessage;
  onReply?: () => void;
}) {
  const senderName = message.auteur?.trim() || "Expéditeur inconnu";
  const senderAddress = message.expediteur?.trim();
  const hasDistinctSenderAddress = !!senderAddress
    && senderAddress.toLowerCase() !== senderName.toLowerCase();
  const attachmentCount = message.nbPiecesJointes || 0;

  return (
    <div className="mt-2 space-y-2 text-sm" data-testid={`client-email-inbound-details-${message.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Badge className="border-teal-200 bg-teal-100 text-teal-800 hover:bg-teal-100 dark:border-teal-800 dark:bg-teal-950 dark:text-teal-200">
          Réponse du client
        </Badge>
        <span className="font-medium text-foreground">{senderName}</span>
        {hasDistinctSenderAddress && (
          <span className="text-muted-foreground break-all">&lt;{senderAddress}&gt;</span>
        )}
        <span className="text-xs text-muted-foreground">
          {message.createdAt ? format(new Date(message.createdAt), "dd/MM/yyyy HH:mm", { locale: fr }) : ""}
        </span>
      </div>
      {message.objet && <p className="font-medium">{message.objet}</p>}
      <p className="whitespace-pre-wrap rounded border border-teal-200/70 bg-background/70 p-3 leading-relaxed dark:border-teal-900">
        {message.contenu}
      </p>
      {attachmentCount > 0 && (
        <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
          {attachmentCount} {attachmentCount === 1 ? "pièce jointe" : "pièces jointes"} — à consulter dans la boîte sav@jobotto.fr.
        </p>
      )}
      {onReply && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onReply}
          data-testid={`button-reply-client-${message.id}`}
        >
          <Reply className="mr-2 h-4 w-4" />
          Répondre au client
        </Button>
      )}
    </div>
  );
}